import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../components/auth/AuthContext";
import {
  Container,
  Typography,
  TextField,
  Grid,
  Card,
  CardContent,
  Button,
  Box,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  Pagination,
  Avatar,
} from "@mui/material";
import {
  Person,
  School,
  ArrowBack,
  Search,
  FilterList,
  Download,
  Restore,
  Visibility,
  TransferWithinAStation,
} from "@mui/icons-material";
import axios from "axios";

const TransferredLearners = () => {
  const navigate = useNavigate();
  const { schoolId, schoolName, user } = useAuth();
  
  const [learners, setLearners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  
  // Transfer dialog states
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [selectedLearners, setSelectedLearners] = useState([]);
  const [transferData, setTransferData] = useState({
    transferReason: "change_of_school",
    remarks: "",
    destinationSchool: "",
  });
  const [transferring, setTransferring] = useState(false);
  
  // Readmission dialog states
  const [readmitDialogOpen, setReadmitDialogOpen] = useState(false);
  const [learnerToReadmit, setLearnerToReadmit] = useState(null);
  const [readmissionData, setReadmissionData] = useState({
    readmissionClass: "",
    readmissionStream: "",
    remarks: "",
  });
  const [readmitting, setReadmitting] = useState(false);
  
  // Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Fetch transferred learners
  const fetchTransferredLearners = async (pageNum = 1, search = "") => {
    try {
      setLoading(true);
      const response = await axios.get(
        `https://ed-9ny1.onrender.com/api/transfers/school/${schoolId}`,
        {
          params: {
            page: pageNum,
            limit: 20,
            search,
          },
        }
      );
      
      if (response.data.success) {
        setLearners(response.data.data);
        setTotalPages(response.data.pagination.pages);
        setTotalCount(response.data.pagination.total);
      }
    } catch (error) {
      console.error("Error fetching transferred learners:", error);
      setSnackbar({
        open: true,
        message: "Failed to load transferred learners",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (schoolId) {
      fetchTransferredLearners(page, searchQuery);
    }
  }, [schoolId, page]);

  const handleSearch = (value) => {
    setSearchQuery(value);
    setPage(1);
    fetchTransferredLearners(1, value);
  };

  const handleSelectLearner = (learnerId) => {
    if (selectedLearners.includes(learnerId)) {
      setSelectedLearners(selectedLearners.filter(id => id !== learnerId));
    } else {
      setSelectedLearners([...selectedLearners, learnerId]);
    }
  };

  const handleSelectAll = () => {
    if (selectedLearners.length === learners.length) {
      setSelectedLearners([]);
    } else {
      setSelectedLearners(learners.map(learner => learner._id));
    }
  };

  const handleOpenTransferDialog = () => {
    if (selectedLearners.length === 0) {
      setSnackbar({
        open: true,
        message: "Please select at least one learner to transfer",
        severity: "warning",
      });
      return;
    }
    setTransferDialogOpen(true);
  };

  const handleTransferLearners = async () => {
    try {
      setTransferring(true);
      const response = await axios.post(
        "https://ed-9ny1.onrender.com/api/transfers/transfer-multiple",
        {
          learnerIds: selectedLearners,
          transferredBy: user._id,
          ...transferData,
        }
      );

      if (response.data.success) {
        setSnackbar({
          open: true,
          message: `Successfully transferred ${response.data.results.successful.length} learners`,
          severity: "success",
        });
        
        // Reset and refresh
        setTransferDialogOpen(false);
        setSelectedLearners([]);
        setTransferData({
          transferReason: "change_of_school",
          remarks: "",
          destinationSchool: "",
        });
        
        fetchTransferredLearners(page, searchQuery);
      }
    } catch (error) {
      console.error("Error transferring learners:", error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Failed to transfer learners",
        severity: "error",
      });
    } finally {
      setTransferring(false);
    }
  };

  const handleOpenReadmitDialog = (learner) => {
    setLearnerToReadmit(learner);
    setReadmissionData({
      readmissionClass: learner.class,
      readmissionStream: learner.stream || "",
      remarks: "",
    });
    setReadmitDialogOpen(true);
  };

  const handleReadmitLearner = async () => {
    if (!learnerToReadmit) return;

    try {
      setReadmitting(true);
      const response = await axios.post(
        `https://ed-9ny1.onrender.com/api/transfers/readmit/${learnerToReadmit._id}`,
        {
          readmittedBy: user._id,
          ...readmissionData,
        }
      );

      if (response.data.success) {
        setSnackbar({
          open: true,
          message: "Learner readmitted successfully",
          severity: "success",
        });
        
        // Reset and refresh
        setReadmitDialogOpen(false);
        setLearnerToReadmit(null);
        fetchTransferredLearners(page, searchQuery);
        
        // Navigate to all students page
        setTimeout(() => {
          navigate("/all-students");
        }, 1500);
      }
    } catch (error) {
      console.error("Error readmitting learner:", error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Failed to readmit learner",
        severity: "error",
      });
    } finally {
      setReadmitting(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getTransferReasonLabel = (reason) => {
    const labels = {
      change_of_school: "Change of School",
      family_relocation: "Family Relocation",
      other: "Other",
    };
    return labels[reason] || reason;
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress color="primary" />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <IconButton onClick={() => navigate(-1)} sx={{ mr: 2 }}>
              <ArrowBack />
            </IconButton>
            <Typography variant="h4" sx={{ fontWeight: "bold", color: "#2c3e50" }}>
              Transferred Learners
            </Typography>
            <Chip
              label={`${totalCount} learners`}
              color="primary"
              sx={{ ml: 2 }}
            />
          </Box>
          
          <Typography variant="subtitle1" color="textSecondary">
            View and manage learners who have been transferred from {schoolName}
          </Typography>
        </Box>

        {/* Search and Actions Bar */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <TextField
            placeholder="Search transferred learners..."
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            variant="outlined"
            size="small"
            sx={{ width: 300 }}
            InputProps={{
              startAdornment: <Search sx={{ mr: 1, color: "action.active" }} />,
            }}
          />

          <Box sx={{ display: "flex", gap: 1 }}>
            {selectedLearners.length > 0 && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<TransferWithinAStation />}
                onClick={handleOpenTransferDialog}
              >
                Transfer Selected ({selectedLearners.length})
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={() => {/* Export functionality */}}
            >
              Export
            </Button>
          </Box>
        </Box>

        {/* Learners Table */}
        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <input
                      type="checkbox"
                      checked={learners.length > 0 && selectedLearners.length === learners.length}
                      indeterminate={selectedLearners.length > 0 && selectedLearners.length < learners.length}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Admission No</TableCell>
                  <TableCell>Class & Stream</TableCell>
                  <TableCell>Transfer Details</TableCell>
                  <TableCell>Parent Contact</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {learners.map((learner) => (
                  <TableRow key={learner._id} hover>
                    <TableCell padding="checkbox">
                      <input
                        type="checkbox"
                        checked={selectedLearners.includes(learner._id)}
                        onChange={() => handleSelectLearner(learner._id)}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Avatar sx={{ mr: 2, bgcolor: "#6200ee" }}>
                          {learner.fullName.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {learner.fullName}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            BC: {learner.birthCertificateNumber}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>{learner.admissionNumber}</TableCell>
                    <TableCell>
                      <Chip
                        label={learner.class}
                        size="small"
                        sx={{ mr: 1 }}
                      />
                      {learner.stream && (
                        <Chip
                          label={learner.stream}
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="caption" display="block">
                          {formatDate(learner.transferDetails.transferDate)}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {getTransferReasonLabel(learner.transferDetails.transferReason)}
                        </Typography>
                        {learner.transferDetails.destinationSchool && (
                          <Typography variant="caption" display="block">
                            To: {learner.transferDetails.destinationSchool}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{learner.parentContact}</TableCell>
                    <TableCell>
                      <Chip
                        label={learner.readmissionDetails?.readmitted ? "Readmitted" : "Transferred"}
                        size="small"
                        color={learner.readmissionDetails?.readmitted ? "success" : "default"}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/transferred-learner-details/${learner._id}`)}
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                        {!learner.readmissionDetails?.readmitted && (
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleOpenReadmitDialog(learner)}
                          >
                            <Restore fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {learners.length === 0 && (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <School sx={{ fontSize: 60, color: "text.disabled", mb: 2 }} />
              <Typography color="textSecondary">
                No transferred learners found
              </Typography>
            </Box>
          )}
        </Card>

        {/* Pagination */}
        {totalPages > 1 && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(e, value) => setPage(value)}
              color="primary"
            />
          </Box>
        )}

        {/* Transfer Dialog */}
        <Dialog
          open={transferDialogOpen}
          onClose={() => !transferring && setTransferDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Transfer Learners</DialogTitle>
          <DialogContent>
            <Alert severity="info" sx={{ mb: 2 }}>
              You are transferring {selectedLearners.length} learner(s). This action will move them to the transferred learners section.
            </Alert>

            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Transfer Reason</InputLabel>
              <Select
                value={transferData.transferReason}
                onChange={(e) => setTransferData({ ...transferData, transferReason: e.target.value })}
                label="Transfer Reason"
              >
                <MenuItem value="change_of_school">Change of School</MenuItem>
                <MenuItem value="family_relocation">Family Relocation</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Destination School (Optional)"
              value={transferData.destinationSchool}
              onChange={(e) => setTransferData({ ...transferData, destinationSchool: e.target.value })}
              margin="normal"
            />

            <TextField
              fullWidth
              label="Remarks"
              value={transferData.remarks}
              onChange={(e) => setTransferData({ ...transferData, remarks: e.target.value })}
              margin="normal"
              multiline
              rows={3}
              placeholder="Add any remarks about this transfer..."
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setTransferDialogOpen(false)}
              disabled={transferring}
            >
              Cancel
            </Button>
            <Button
              onClick={handleTransferLearners}
              variant="contained"
              color="primary"
              disabled={transferring}
            >
              {transferring ? (
                <CircularProgress size={24} />
              ) : (
                `Transfer ${selectedLearners.length} Learner(s)`
              )}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Readmission Dialog */}
        <Dialog
          open={readmitDialogOpen}
          onClose={() => !readmitting && setReadmitDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Readmit Learner</DialogTitle>
          <DialogContent>
            {learnerToReadmit && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                You are about to readmit <strong>{learnerToReadmit.fullName}</strong> back to active learners.
              </Alert>
            )}

            <TextField
              fullWidth
              label="Class for Readmission"
              value={readmissionData.readmissionClass}
              onChange={(e) => setReadmissionData({ ...readmissionData, readmissionClass: e.target.value })}
              margin="normal"
              required
            />

            <TextField
              fullWidth
              label="Stream (Optional)"
              value={readmissionData.readmissionStream}
              onChange={(e) => setReadmissionData({ ...readmissionData, readmissionStream: e.target.value })}
              margin="normal"
            />

            <TextField
              fullWidth
              label="Remarks"
              value={readmissionData.remarks}
              onChange={(e) => setReadmissionData({ ...readmissionData, remarks: e.target.value })}
              margin="normal"
              multiline
              rows={3}
              placeholder="Add any remarks about this readmission..."
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setReadmitDialogOpen(false)}
              disabled={readmitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleReadmitLearner}
              variant="contained"
              color="primary"
              disabled={readmitting}
            >
              {readmitting ? (
                <CircularProgress size={24} />
              ) : (
                "Readmit Learner"
              )}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          message={snackbar.message}
          sx={{
            bgcolor: snackbar.severity === "error" ? "#e74c3c" : "#4CAF50",
          }}
        />
      </motion.div>
    </Container>
  );
};

export default TransferredLearners;